import { ColorCategories } from '@/api/api';
import { Trash2, Plus } from 'lucide-react';

interface ColoringCategoriesFormProps {
  coloring: ColorCategories;
  onChange: (coloring: ColorCategories) => void;
  disabled?: boolean;
}

export default function ColoringCategoriesForm({ coloring, onChange, disabled }: ColoringCategoriesFormProps) {
  const entries = Object.entries(coloring.colorMap);

  const addCategory = () => {
    const newColorMap = {
      ...coloring.colorMap,
      [`category${entries.length + 1}`]: { color: '#000000', legend: '' }
    };
    onChange({ ...coloring, colorMap: newColorMap });
  };

  const removeCategory = (key: string) => {
    const newColorMap = { ...coloring.colorMap };
    delete newColorMap[key];
    onChange({ ...coloring, colorMap: newColorMap });
  };

  const updateCategoryKey = (oldKey: string, newKey: string) => {
    if (oldKey === newKey) return;
    if (newKey in coloring.colorMap) {
      alert('Category name already exists');
      return;
    }
    const newColorMap = { ...coloring.colorMap };
    newColorMap[newKey] = newColorMap[oldKey];
    delete newColorMap[oldKey];
    onChange({ ...coloring, colorMap: newColorMap });
  };

  const updateCategory = (key: string, field: 'color' | 'legend', value: string) => {
    const newColorMap = {
      ...coloring.colorMap,
      [key]: { ...coloring.colorMap[key], [field]: value }
    };
    onChange({ ...coloring, colorMap: newColorMap });
  };

  return (
    <div className="space-y-3 bg-purple-50 p-3 rounded">
      <div>
        <label className="text-xs font-medium mb-1 block">Fallback Color</label>
        <input
          type="color"
          value={coloring.fallback}
          onChange={(e) => onChange({ ...coloring, fallback: e.target.value })}
          disabled={disabled}
          className="w-full h-10 px-1 py-1 border rounded"
        />
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="text-xs font-medium">Categories</label>
          <button
            type="button"
            onClick={addCategory}
            disabled={disabled}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          >
            <Plus className="w-3 h-3" />
            Add Category
          </button>
        </div>

        <div className="space-y-2">
          {entries.map(([key, category]) => (
            <div key={key} className="bg-white p-2 rounded border space-y-2">
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-xs font-medium mb-1 block">Category Name</label>
                  <input
                    type="text"
                    value={key}
                    onChange={(e) => updateCategoryKey(key, e.target.value)}
                    disabled={disabled}
                    className="w-full px-2 py-1 border rounded text-sm"
                    placeholder="Category name"
                  />
                </div>
                <div className="w-20">
                  <label className="text-xs font-medium mb-1 block">Color</label>
                  <input
                    type="color"
                    value={category.color}
                    onChange={(e) => updateCategory(key, 'color', e.target.value)}
                    disabled={disabled}
                    className="w-full h-8 px-1 py-1 border rounded"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeCategory(key)}
                    disabled={disabled}
                    className="px-2 py-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Legend Label</label>
                <input
                  type="text"
                  value={category.legend}
                  onChange={(e) => updateCategory(key, 'legend', e.target.value)}
                  disabled={disabled}
                  className="w-full px-2 py-1 border rounded text-sm"
                  placeholder="Label"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
